/**
 * Service to handle term updates in the spec editor
 */
export class SpecTermUpdateService {
    constructor(termsStore) {
        this.termsStore = termsStore;
        this.editors = new Map(); // Map<string, Editor>
        this.unsubscribe = null;
    }

    /**
     * Register an editor to receive term updates
     * @param {string} id - Unique identifier for the editor
     * @param {Editor} editor - The editor instance
     */
    registerEditor(id, editor) {
        this.editors.set(id, editor);
        
        // Set up store subscription if this is the first editor
        if (this.editors.size === 1) {
            this.setupStoreSubscription();
        }
    }

    /**
     * Unregister an editor
     * @param {string} id - Editor identifier to remove
     */
    unregisterEditor(id) {
        this.editors.delete(id);
        
        // Clean up subscription if no editors remain
        if (this.editors.size === 0 && this.unsubscribe) {
            this.unsubscribe();
            this.unsubscribe = null;
        }
    }

    /**
     * Set up subscription to terms store
     */
    setupStoreSubscription() {
        let oldEditingTerm = null;

        this.unsubscribe = this.termsStore.subscribe(store => {
            // Only log significant changes
            const termsChanged = this.previousTermEntries && 
                (store.termEntries.size !== this.previousTermEntries.size ||
                 ![...store.termEntries.keys()].every(key => this.previousTermEntries.has(key)));
            
            if (termsChanged || (oldEditingTerm !== store.editingTerm)) {
                console.log('[SpecTermUpdateService] Terms changed:', {
                    editingTermChanged: oldEditingTerm !== store.editingTerm,
                    termsCountChanged: this.previousTermEntries ? store.termEntries.size !== this.previousTermEntries.size : false,
                    currentTermsCount: store.termEntries ? store.termEntries.size : 0
                });
            }

            // Detect term edit completion
            if (oldEditingTerm && !store.editingTerm) {
                const oldTerm = oldEditingTerm;
                // Find new term by checking what's new in termEntries
                const newTermEntry = Array.from(store.termEntries.keys())
                    .find(key => !this.previousTermEntries?.has(key));
                const newTerm = newTermEntry ? store.termEntries.get(newTermEntry)?.parent_term : null;
                
                console.log('[SpecTermUpdateService] Detected term change:', {
                    oldTerm,
                    newTerm,
                    termEntriesKeys: Array.from(store.termEntries.keys())
                });

                if (oldTerm && newTerm) {
                    this.updateTermInEditors(oldTerm, newTerm);
                }
            }

            // Track state for next update
            oldEditingTerm = store.editingTerm;
            this.previousTermEntries = new Set(store.termEntries.keys());
        });
    }

    /**
     * Update a term in all registered editors
     * @param {string} oldTerm - Term to replace
     * @param {string} newTerm - New term to use
     */
    updateTermInEditors(oldTerm, newTerm) {
        console.log('[SpecTermUpdateService] Starting term update in editors:', {
            oldTerm,
            newTerm,
            registeredEditors: Array.from(this.editors.keys())
        });

        for (const [editorId, editor] of this.editors.entries()) {
            if (!editor || editor.isDestroyed) {
                console.log(`[SpecTermUpdateService] Skipping editor ${editorId} - invalid or destroyed`);
                continue;
            }

            const { state, view } = editor;
            const { tr } = state;
            
            console.log(`[SpecTermUpdateService] Processing editor ${editorId}`);
            console.log('[SpecTermUpdateService] Current document text:', state.doc.textContent);

            // Find all matches first, then apply replacements
            const matches = [];
            
            // Collect all matches first
            state.doc.descendants((node, pos) => {
                if (node.isText) {
                    const text = node.text;
                    const escapedTerm = this.escapeRegExp(oldTerm);
                    const termRegex = new RegExp(`\\b${escapedTerm}\\b`, 'gi');
                    
                    let match;
                    while ((match = termRegex.exec(text)) !== null) {
                        matches.push({
                            actualTerm: match[0],
                            startIndex: match.index,
                            absoluteStart: pos + match.index,
                            length: match[0].length,
                            text: text
                        });
                    }
                }
            });

            // Sort matches by position in reverse order (to maintain position validity)
            matches.sort((a, b) => b.absoluteStart - a.absoluteStart);

            console.log(`[SpecTermUpdateService] Found ${matches.length} matches to replace`);

            // Apply all replacements in reverse order
            for (const match of matches) {
                const preserveCase = match.actualTerm !== match.actualTerm.toLowerCase();
                const replacementTerm = preserveCase ? 
                    this.matchCase(newTerm, match.actualTerm) : 
                    newTerm;

                console.log('[SpecTermUpdateService] Replacing:', {
                    term: match.actualTerm,
                    at: match.absoluteStart,
                    with: replacementTerm
                });

                tr.replaceWith(
                    match.absoluteStart,
                    match.absoluteStart + match.length,
                    state.schema.text(replacementTerm)
                );
            }

            if (matches.length > 0) {
                view.dispatch(tr);
                console.log(`[SpecTermUpdateService] Applied ${matches.length} replacements`);
            }
        }
    }

    /**
     * Match the case pattern of the original term
     * @param {string} newTerm - The new term to adjust
     * @param {string} originalTerm - The original term to match case from
     * @returns {string} - The new term with matched case
     */
    matchCase(newTerm, originalTerm) {
        // Handle empty terms
        if (!originalTerm || !newTerm) return newTerm;

        // If original is all uppercase, convert new term to uppercase
        if (originalTerm === originalTerm.toUpperCase() && 
            originalTerm !== originalTerm.toLowerCase()) { // Ensure it's not just symbols
            return newTerm.toUpperCase();
        }
        
        // If original has first letter capitalized
        if (originalTerm[0] === originalTerm[0].toUpperCase() && 
            originalTerm !== originalTerm.toLowerCase()) { // Ensure it's not just symbols
            // Split into words
            const words = originalTerm.split(/[-_.]/);
            const newWords = newTerm.split(/[-_.]/);
            
            // Match case for each word
            const matchedWords = newWords.map((word, i) => {
                const originalWord = words[i] || words[0]; // Use first word pattern if no matching word
                if (originalWord === originalWord.toUpperCase()) {
                    return word.toUpperCase();
                }
                if (originalWord[0] === originalWord[0].toUpperCase()) {
                    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
                }
                return word.toLowerCase();
            });
            
            // Preserve original separators
            let result = '';
            let separatorIndex = 0;
            for (let i = 0; i < originalTerm.length; i++) {
                const char = originalTerm[i];
                if (char === '-' || char === '_' || char === '.') {
                    result += char;
                } else if (separatorIndex < matchedWords.length) {
                    if (i === 0 || originalTerm[i-1].match(/[-_.]/)) {
                        result += matchedWords[separatorIndex++];
                    }
                }
            }
            return result || matchedWords.join('_'); // Fallback to underscore if no separators found
        }
        
        // Default to lowercase
        return newTerm.toLowerCase();
    }

    /**
     * Escape special characters in string for use in regex
     * @param {string} string - String to escape
     * @returns {string} - Escaped string safe for regex
     */
    escapeRegExp(string) {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    /**
     * Clean up subscriptions
     */
    destroy() {
        if (this.unsubscribe) {
            this.unsubscribe();
            this.unsubscribe = null;
        }
    }
}
